/**
 * Pricing Chart Section Component
 * Orchestrates chart, series selector, and timeframe selector
 */

import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { pricingService } from '../services/pricing.service.js';
import { environment } from '../environments/environment.js';
import type {
  PricingResponse,
  SeriesName,
  Timeframe,
  ChartSeries,
} from '../types/pricing.js';
import type { SeriesChangeEvent } from './series-selector.js';
import type { TimeframeChangeEvent } from './timeframe-selector.js';
import './series-selector.js';
import './timeframe-selector.js';
import './pricing-chart.js';
import './latest-prices.js';

@customElement('pricing-chart-section')
export class PricingChartSection extends LitElement {
  // Use Light DOM for consistency
  protected createRenderRoot() {
    return this;
  }

  @state()
  private allTimeframeData: Map<Timeframe, PricingResponse> = new Map();

  @state()
  private availableSeries: SeriesName[] = [];

  @state()
  private selectedSeries: SeriesName[] = [];

  @state()
  private timeframe: Timeframe = 'd';

  @state()
  private loading = true;

  @state()
  private error?: string;

  @state()
  private chartSeries: ChartSeries[] = [];

  /**
   * Fetch all timeframe data on first render
   */
  protected async firstUpdated() {
    // Skip data fetching if the feature is disabled
    if (!environment.features?.enablePricingChart) {
      return;
    }

    await this.fetchAllTimeframes();
  }

  /**
   * Fetch all timeframe data in parallel
   */
  private async fetchAllTimeframes() {
    this.loading = true;
    this.error = undefined;

    try {
      const timeframes: Timeframe[] = ['h', 'd', 'w', 'm'];

      // Fetch all timeframes in parallel
      const results = await Promise.all(
        timeframes.map(async (tf) => ({
          timeframe: tf,
          data: await pricingService.fetchPricing(tf),
        }))
      );

      // Store all results in the map
      results.forEach(({ timeframe, data }) => {
        this.allTimeframeData.set(timeframe, data);
      });

      // Extract available series from the current timeframe data
      const currentData = this.allTimeframeData.get(this.timeframe);
      if (currentData?.metadata?.series_names) {
        this.availableSeries = currentData.metadata.series_names;

        // Initialize selected series with the first series if none selected
        if (this.selectedSeries.length === 0 && this.availableSeries.length > 0) {
          this.selectedSeries = [this.availableSeries[0]];
        }
      }

      this.updateChartSeries();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : 'Failed to load chart data';
      console.error('Error fetching pricing data:', err);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Update chart series based on selected series and data
   */
  private updateChartSeries() {
    const currentData = this.allTimeframeData.get(this.timeframe);

    if (!currentData) {
      this.chartSeries = [];
      return;
    }

    this.chartSeries = pricingService.transformToChartSeries(
      currentData,
      this.selectedSeries
    );
  }

  /**
   * Handle series selection change
   */
  private handleSeriesChange(event: CustomEvent<SeriesChangeEvent>) {
    this.selectedSeries = event.detail.selectedSeries;
    this.updateChartSeries();
  }

  /**
   * Handle timeframe change
   */
  private handleTimeframeChange(
    event: CustomEvent<TimeframeChangeEvent>
  ) {
    this.timeframe = event.detail.timeframe;
    this.updateChartSeries();
  }

  /**
   * Render loading state
   */
  private renderLoading() {
    return html`
      <div
        class="flex items-center justify-center min-h-[300px] md:min-h-[400px] lg:min-h-[500px]"
      >
        <div class="text-center">
          <div
            class="inline-block w-8 h-8 border-4 border-surface-border border-t-brand-primary rounded-full animate-spin"
          ></div>
          <p class="mt-4 text-text-secondary text-sm">Loading chart data...</p>
        </div>
      </div>
    `;
  }

  /**
   * Render error state
   */
  private renderError() {
    return html`
      <div
        class="flex items-center justify-center min-h-[300px] md:min-h-[400px] lg:min-h-[500px]"
      >
        <div class="text-center max-w-md">
          <svg
            class="mx-auto w-12 h-12 text-error mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p class="text-text-primary font-medium mb-2">
            Failed to load chart
          </p>
          <p class="text-text-secondary text-sm mb-4">${this.error}</p>
          <button
            @click="${this.fetchAllTimeframes}"
            class="px-4 py-2 bg-brand-primary text-white text-sm hover:opacity-80 transition-opacity duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Render chart
   */
  private renderChart() {
    return html`
      <pricing-chart .chartSeries="${this.chartSeries}"></pricing-chart>
    `;
  }

  render() {
    return html`
      <section class="py-16 px-4 lg:px-16">
        <div class="container mx-auto">
          <!-- Section Header -->
          <div class="mb-8">
            <h2
              class="font-serif text-3xl md:text-4xl text-text-primary mb-2"
            >
              Pricing Trends
            </h2>
            <p class="text-text-secondary md:text-lg">
              Real-time GPU compute pricing across providers
            </p>
          </div>

          <!-- Latest Prices Display -->
          ${!this.loading && !this.error
            ? html`
                <latest-prices
                  .chartSeries="${this.chartSeries}"
                  .timeframe="${this.timeframe}"
                ></latest-prices>
              `
            : ''}

          <!-- Controls Row -->
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
          >
            <!-- Series Selector -->
            <series-selector
              .availableSeries="${this.availableSeries}"
              .selectedSeries="${this.selectedSeries}"
              @series-change="${this.handleSeriesChange}"
            ></series-selector>

            <!-- Timeframe Selector -->
            <timeframe-selector
              .selectedTimeframe="${this.timeframe}"
              @timeframe-change="${this.handleTimeframeChange}"
            ></timeframe-selector>
          </div>

          <!-- Chart Container -->
          <div class="bg-surface-elevated border border-surface-border p-4">
            ${this.loading
              ? this.renderLoading()
              : this.error
                ? this.renderError()
                : this.renderChart()}
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pricing-chart-section': PricingChartSection;
  }
}
